/**
 * useInvoiceAutoAnalysisJob — hook do backend-driven auto-analizy Agenta Faktur.
 *
 * Analogiczny do `useBankAutoAnalysisJob`. Patrz raport:
 * /app/docs/INVOICE_AGENT_BACKEND_AUTO_ANALYSIS_JOB_IMPLEMENTATION.md
 *
 * Lifecycle:
 *  1. mount → GET /auto-analysis-jobs/active — jeśli aktywny job istnieje,
 *     hook od razu przejmuje polling.
 *  2. start() → POST /start → trzyma job_id, polling co 2s.
 *  3. polling → terminal status → onComplete callback.
 *  4. cancel() → POST /{job_id}/cancel.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

// Polling: pierwsze odświeżenie po 30s od startu, potem co 60s.
const INITIAL_POLL_DELAY_MS = 30_000;
const POLL_INTERVAL_MS = 60_000;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const isActiveStatus = (s) => s === "queued" || s === "running";
const isTerminalStatus = (s) =>
  s === "completed" || s === "failed" || s === "cancelled";

export function useInvoiceAutoAnalysisJob({ onComplete } = {}) {
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [initialChecking, setInitialChecking] = useState(true);
  const pollTimerRef = useRef(null);
  const initialTimerRef = useRef(null);
  const lastTerminalIdRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (initialTimerRef.current) {
      clearTimeout(initialTimerRef.current);
      initialTimerRef.current = null;
    }
  }, []);

  const handleTerminal = useCallback((finalJob) => {
    stopPolling();
    if (finalJob && lastTerminalIdRef.current !== finalJob.job_id) {
      lastTerminalIdRef.current = finalJob.job_id;
      try { onCompleteRef.current?.(finalJob); } catch (_) { /* ignore */ }
    }
  }, [stopPolling]);

  const pollOnce = useCallback(async (jobId) => {
    try {
      const r = await axios.get(
        `${API}/ai/agents/invoice/auto-analysis-jobs/${jobId}`,
        { withCredentials: true }
      );
      const next = r.data?.job;
      if (next) {
        setJob(next);
        if (isTerminalStatus(next.status)) handleTerminal(next);
      }
    } catch (e) {
      if (e?.response?.status === 404) {
        stopPolling();
        setJob(null);
      }
    }
  }, [handleTerminal, stopPolling]);

  const startPolling = useCallback((jobId) => {
    stopPolling();
    initialTimerRef.current = setTimeout(() => {
      pollOnce(jobId);
      pollTimerRef.current = setInterval(() => pollOnce(jobId), POLL_INTERVAL_MS);
    }, INITIAL_POLL_DELAY_MS);
  }, [pollOnce, stopPolling]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/ai/agents/invoice/auto-analysis-jobs/active`,
          { withCredentials: true }
        );
        if (!alive) return;
        const active = r.data?.job;
        if (active && isActiveStatus(active.status)) {
          setJob(active);
          startPolling(active.job_id);
        }
      } catch (_) { /* milcz */ } finally {
        if (alive) setInitialChecking(false);
      }
    })();
    return () => { alive = false; stopPolling(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = useCallback(async () => {
    if (starting) return null;
    if (job && isActiveStatus(job.status)) return job;
    setStarting(true);
    try {
      const r = await axios.post(
        `${API}/ai/agents/invoice/auto-analysis-jobs/start`,
        {},
        { withCredentials: true }
      );
      const newJob = r.data?.job;
      setJob(newJob);
      if (newJob && isActiveStatus(newJob.status)) {
        startPolling(newJob.job_id);
      } else if (newJob && isTerminalStatus(newJob.status)) {
        handleTerminal(newJob);
      }
      return newJob;
    } finally {
      setStarting(false);
    }
  }, [starting, job, startPolling, handleTerminal]);

  const cancel = useCallback(async () => {
    if (!job?.job_id) return;
    try {
      await axios.post(
        `${API}/ai/agents/invoice/auto-analysis-jobs/${job.job_id}/cancel`,
        {},
        { withCredentials: true }
      );
    } catch (_) { /* ignore */ }
  }, [job]);

  const running = !!job && isActiveStatus(job.status);
  const cancelRequested = !!(job && job.cancel_requested);
  const lastError = (job?.errors && job.errors.length > 0)
    ? job.errors[job.errors.length - 1]
    : null;

  return {
    job,
    running,
    cancelRequested,
    starting,
    initialChecking,
    start,
    cancel,
    progress: { current: job?.processed ?? 0, total: job?.total ?? 0 },
    analyzed: job?.analyzed ?? 0,
    cached: job?.cached ?? 0,
    failed: job?.failed ?? 0,
    // P0 (2026-02 eligibility fix) — liczba kontrahentów pominiętych
    // (przeniesionych do „Pozostałych" w trakcie joba). Nie jest błędem.
    // Fallback 0 dla starych dokumentów sprzed migracji.
    skipped: job?.skipped ?? 0,
    lastSkipReason: job?.last_skip_reason || null,
    currentVendorNip: job?.current_vendor_nip || "",
    currentVendorName: job?.current_vendor_name || "",
    lastUpdatedAt: job?.updated_at || null,
    lastError,
  };
}
